import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  createManualUnit,
  upsertUnitAttributes,
} from "@/api/configuration-server";
import { errorDetailOr } from "@/lib/api-error";
import {
  orgUnitFormFor,
  type OrgUnitFormDefinition,
  type OrgUnitFormField,
} from "@/lib/org-unit-forms";
import type { ManualUnitAttributes } from "@/api/configuration-server";
import {
  effectiveOrgId,
  extractedValueOf,
  type OrgTreeNode,
} from "@/lib/org-structure-tree";

/** Unidade candidata a pai, para o select "ID – Nome" (#899). */
export interface OrgParentCandidate {
  /** Id de ligação — é ele que vai em `parent_id`. */
  id: string;
  name: string | null;
  /** Id corrigido pelo consultor, quando houver — é o que o select mostra. */
  targetId?: string | null;
}

const FIELD_LABEL: Record<OrgUnitFormField["key"], string> = {
  name: "Name",
  city: "City",
  country: "Country",
  subtype: "Subtype",
};

/** "ID – Nome", ou só "ID" quando o nome não foi extraído (#899). */
function parentOptionLabel(candidate: OrgParentCandidate): string {
  const id = candidate.targetId ?? candidate.id;
  return candidate.name ? `${id} – ${candidate.name}` : id;
}

// Referência estável para a criação: um `{}` novo a cada render dispararia o
// efeito de reset abaixo, que devolveria o seletor de nível ao inicial.
const NO_INITIAL_VALUES: Record<string, string> = {};

/** Valor atual de um campo do formulário (já com a correção, se houver). */
function fieldValueOf(node: OrgTreeNode, key: OrgUnitFormField["key"]): string {
  const fieldValue: Record<OrgUnitFormField["key"], string | null> = {
    name: node.name,
    city: node.city,
    country: node.country,
    subtype: node.subtype,
  };
  return fieldValue[key] ?? "";
}

// Campos da unidade extraída do SAP que se preenchem aqui quando a extração os trouxe vazios
// (o mesmo conjunto que o api aceita).
const FILLABLE_WHEN_EMPTY: ReadonlyArray<OrgUnitFormField["key"]> = [
  "name",
  "city",
  "country",
];

/** O campo veio vazio da extração (com a sobreposição do consultor valendo o original)? */
function cameEmpty(node: OrgTreeNode, key: OrgUnitFormField["key"]): boolean {
  if (!FILLABLE_WHEN_EMPTY.includes(key)) return false;
  const original = extractedValueOf(node, key);
  return original ? !original.value : fieldValueOf(node, key) === "";
}

/**
 * Valor atual de cada campo editável, com que a edição abre. Da unidade
 * extraída do SAP entram o ID e os campos que a extração trouxe vazios.
 */
function currentValues(
  node: OrgTreeNode,
  formDef: OrgUnitFormDefinition,
  hasParentField: boolean,
  idOnly: boolean,
): Record<string, string> {
  const values: Record<string, string> = {};
  if (formDef.idMaxLength > 0) values.id = effectiveOrgId(node);
  if (idOnly) {
    for (const field of formDef.fields) {
      if (cameEmpty(node, field.key))
        values[field.key] = fieldValueOf(node, field.key);
    }
    return values;
  }
  for (const field of formDef.fields)
    values[field.key] = fieldValueOf(node, field.key);
  if (hasParentField) {
    if (formDef.multiParent)
      values.parent_ids = (node.associations ?? [])
        .map((item) => item.id)
        .join(",");
    else values.parent_id = node.parentId ?? "";
  }
  return values;
}

/**
 * Cria unidade que a extração não trouxe, ou edita uma existente — o MESMO
 * formulário nos dois casos. O formulário é POR TIPO (#899, S-079): cada um
 * dos 9 tipos do catálogo do CBC (`org-unit-forms.ts`) tem seus próprios
 * campos, tamanhos e opções. Tipo fora dos 9 (sales_area, division, …) só
 * nasce extraído — a criação de manual recusa esse tipo: ele edita só o ID,
 * quando o tipo tem regra de ID (`idMaxLength`), e o nome fica à vista, travado.
 *
 * A edição abre com os valores atuais e envia só o que mudou. Apagar um
 * campo não envia vazio. Na unidade extraída do SAP só o ID se edita (spec
 * 2026-10-26, D31): os outros campos ficam à vista, travados, e o ID gravado
 * vira SOBREPOSIÇÃO, com a extração intacta; o "Restore extracted values" do
 * rodapé desfaz essa sobreposição. Na manual, cada campo é o próprio dado.
 *
 * O id da unidade manual é a chave dela, e os filhos guardam esse id — por
 * isso, com filhos, o campo fica travado (o backend recusa do mesmo jeito).
 */
export function ManualUnitDialog({
  open,
  mode,
  projectId,
  levelCode,
  node,
  levelOptions = [],
  parentLevelCodeByType = {},
  parentCandidatesByLevel = {},
  parentControls,
  onParentLevelChange,
  hasChildren,
  onSaved,
  onOpenChange,
}: {
  open: boolean;
  mode: "create" | "edit";
  projectId: string;
  /** Nível default/único do modo edit. No modo create, é só o valor inicial
   *  do seletor abaixo — `selectedLevelCode` é quem manda no que é enviado. */
  levelCode: string;
  node?: OrgTreeNode;
  /** Níveis disponíveis para o seletor do modo create (#845), restritos aos 9
   *  tipos do catálogo do CBC que aceitam unidade manual (#899). */
  levelOptions?: Array<{ levelCode: string; levelName: string }>;
  /** `level_code` → `parent_level_code` do cookbook inteiro (não só os
   *  níveis com dado extraído) — de onde vem a lista de candidatos a pai. */
  parentLevelCodeByType?: Record<string, string | null>;
  /** `level_code` → unidades desse nível (extraídas + manuais), para o
   *  select de pai "ID – Nome". */
  parentCandidatesByLevel?: Record<string, OrgParentCandidate[]>;
  parentControls?: React.ReactNode;
  hasChildren?: boolean;
  onParentLevelChange?: (level: string | null) => void;
  onSaved: () => void;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const isEdit = mode === "edit" && node !== undefined;
  const [itemKey, setItemKey] = useState("");
  const [selectedLevelCode, setSelectedLevelCode] = useState(levelCode);
  const typeCode = isEdit ? node.levelCode : selectedLevelCode;
  const formDef = orgUnitFormFor(typeCode);
  const isManualOrigin = !isEdit || node.origin === "manual";
  const idOnly = !isManualOrigin;
  const hasParentField = formDef.hasParent && isManualOrigin;
  const parentLevelForType = parentLevelCodeByType[typeCode] ?? null;
  useEffect(() => {
    if (open) onParentLevelChange?.(idOnly ? null : parentLevelForType);
  }, [open, parentLevelForType, onParentLevelChange, idOnly]);
  const parentCandidates = parentLevelForType
    ? (parentCandidatesByLevel[parentLevelForType] ?? [])
    : [];
  const idLocked =
    isEdit &&
    node.origin === "manual" &&
    (hasChildren ?? node.children.length > 0);
  const initialValues = useMemo(
    () =>
      isEdit
        ? currentValues(node, formDef, hasParentField, idOnly)
        : NO_INITIAL_VALUES,
    [isEdit, node, formDef, hasParentField, idOnly],
  );
  // `null` é um valor de verdade aqui: significa "remova a sobreposição deste
  // campo" (Restore extracted values). Sem ele, uma correção feita por engano ficava
  // para sempre — o merge de JSONB no backend só acrescenta chaves.
  const [values, setValues] = useState<Partial<Record<string, string | null>>>(
    {},
  );
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Reseta os campos toda vez que o modal É ABERTO — a MESMA instância deste
  // componente é reaproveitada entre unidades diferentes (`OrgStructureTree`
  // troca só as props, nunca desmonta o diálogo), então sem isto o segundo
  // "Edit" clicado herdava o rascunho digitado no primeiro.
  useEffect(() => {
    if (!open) return;
    setItemKey("");
    setSelectedLevelCode(levelCode);
    setValues(initialValues);
    setError(null);
  }, [open, levelCode, initialValues]);

  // Na criação nenhum select nasce escolhido (subtype nem pai): o subtipo não
  // pode ser trocado depois de criado no CBC, e pré-selecionar a primeira
  // opção seria decidir pelo consultor sem ele perceber.
  function valueOf(key: string): string {
    return values[key] ?? "";
  }

  const missingRequiredField =
    formDef.fields.some((field) => valueOf(field.key).trim() === "") ||
    (hasParentField &&
      valueOf(formDef.multiParent ? "parent_ids" : "parent_id") === "");

  const idsOf = (csv: string): string[] =>
    csv
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);

  function buildCreateAttributes(): ManualUnitAttributes {
    const attributes: Record<string, string | string[] | null> = {};
    for (const field of formDef.fields) {
      attributes[field.key] = valueOf(field.key).trim();
    }
    if (hasParentField) {
      if (formDef.multiParent)
        attributes.parent_ids = idsOf(valueOf("parent_ids"));
      else attributes.parent_id = valueOf("parent_id").trim();
    }
    return attributes as ManualUnitAttributes;
  }

  function buildEditAttributes(): ManualUnitAttributes & {
    id?: string | null;
  } {
    const attributes: Record<string, string | string[] | null> = {};
    for (const key of Object.keys(initialValues)) {
      const value = values[key];
      if (value === null) {
        attributes[key] = null;
        continue;
      }
      const trimmed = (value ?? "").trim();
      if (trimmed === "" || trimmed === initialValues[key]) continue;
      attributes[key] = key === "parent_ids" ? idsOf(trimmed) : trimmed;
    }
    return attributes as ManualUnitAttributes & { id?: string | null };
  }

  const editAttributes = isEdit ? buildEditAttributes() : {};
  const saveDisabled =
    saving ||
    (isEdit
      ? Object.keys(editAttributes).length === 0
      : itemKey.trim() === "" || missingRequiredField);

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      if (isEdit) {
        await upsertUnitAttributes(
          projectId,
          levelCode,
          node.itemKey,
          editAttributes,
        );
        // `id: null` é o "Restore extracted values": o ID que vale agora é o
        // extraído, e não o sobreposto que acabou de ser desfeito.
        const unitId =
          editAttributes.id === null
            ? (extractedValueOf(node, "id")?.value ?? node.id)
            : (editAttributes.id ?? effectiveOrgId(node));
        toast.success(t("{{unit}} updated.", { unit: unitId }));
      } else {
        await createManualUnit(
          projectId,
          selectedLevelCode,
          itemKey.trim(),
          buildCreateAttributes(),
        );
        toast.success(t("{{unit}} created.", { unit: itemKey.trim() }));
      }
      onSaved();
      onOpenChange(false);
    } catch (err) {
      // A mensagem do 409 (id duplicado) é o que leva o consultor até a
      // unidade existente — o modal fica aberto de propósito. `errorDetailOr`
      // (lib/api-error.ts) é o único lugar que conhece o envelope do axios
      // (`err.response.data.detail`) — reusado aqui em vez de reimplementar a
      // leitura à mão.
      setError(errorDetailOr(err, t("Something went wrong. Try again.")));
    } finally {
      setSaving(false);
    }
  }

  // Campos corrigidos de uma unidade extraída que esta edição envia (só o ID) —
  // o que o "Restore extracted values" do rodapé devolve à extração.
  const restorableKeys = isEdit
    ? Object.keys(initialValues).filter(
        (key) => extractedValueOf(node, key) !== null,
      )
    : [];
  const restored =
    restorableKeys.length > 0 &&
    restorableKeys.every((key) => values[key] === null);

  function restoreExtractedValues() {
    setValues((current) => ({
      ...current,
      ...Object.fromEntries(restorableKeys.map((key) => [key, null])),
    }));
  }

  /** Valor mostrado num campo cuja correção foi desfeita: o da extração. */
  function restoredValue(key: string): string {
    return (isEdit ? extractedValueOf(node, key)?.value : null) ?? "";
  }

  function renderField(field: OrgUnitFormField) {
    const cleared = values[field.key] === null;
    const label = t(FIELD_LABEL[field.key]);

    if (isEdit && idOnly && !cameEmpty(node, field.key)) {
      const raw = fieldValueOf(node, field.key);
      const option = field.options?.find(
        (candidate) => candidate.value === raw,
      );
      return (
        <div key={field.key} className="space-y-1">
          <Label htmlFor={`unit-${field.key}`}>{label}</Label>
          <Input
            id={`unit-${field.key}`}
            value={
              option ? t(option.label, { defaultValue: option.label }) : raw
            }
            disabled
            readOnly
          />
        </div>
      );
    }

    return (
      <div key={field.key} className="space-y-1">
        <Label htmlFor={`unit-${field.key}`}>{label}</Label>
        {field.options ? (
          <select
            id={`unit-${field.key}`}
            value={cleared ? restoredValue(field.key) : valueOf(field.key)}
            disabled={cleared}
            onChange={(e) =>
              setValues((current) => ({
                ...current,
                [field.key]: e.target.value,
              }))
            }
            className="flex h-10 w-full items-center rounded-md border border-input bg-background px-3 py-2 text-sm"
          >
            <option value="">{t("Select…")}</option>
            {field.options.map((option) => (
              <option key={option.value} value={option.value}>
                {t(option.label, { defaultValue: option.label })}
              </option>
            ))}
          </select>
        ) : (
          <Input
            id={`unit-${field.key}`}
            value={cleared ? restoredValue(field.key) : valueOf(field.key)}
            maxLength={field.maxLength}
            disabled={cleared}
            onChange={(e) =>
              setValues((current) => ({
                ...current,
                [field.key]:
                  field.key === "country"
                    ? e.target.value.toUpperCase()
                    : e.target.value,
              }))
            }
          />
        )}
      </div>
    );
  }

  function renderIdField() {
    if (!isEdit) {
      return (
        <div className="space-y-1">
          <Label htmlFor="unit-id">{t("ID")}</Label>
          <Input
            id="unit-id"
            value={itemKey}
            maxLength={formDef.idMaxLength || undefined}
            onChange={(e) => setItemKey(e.target.value)}
          />
        </div>
      );
    }
    if (formDef.idMaxLength === 0) return null;
    const cleared = values.id === null;
    return (
      <div className="space-y-1">
        <Label htmlFor="unit-id">{t("ID")}</Label>
        <Input
          id="unit-id"
          value={cleared ? restoredValue("id") : valueOf("id")}
          maxLength={formDef.idMaxLength}
          disabled={cleared || idLocked}
          onChange={(e) =>
            setValues((current) => ({ ...current, id: e.target.value }))
          }
        />
        {formDef.idHint && (
          <p className="text-xs text-muted-foreground">{t(formDef.idHint)}</p>
        )}
        {idLocked && (
          <p className="text-xs text-muted-foreground">
            {t(
              "This unit has linked units below it, so its ID can no longer be changed.",
            )}
          </p>
        )}
      </div>
    );
  }

  function renderParentField() {
    if (!hasParentField) return null;
    if (formDef.multiParent) return renderParentsField();
    if (parentCandidates.length === 0) {
      return (
        <p className="text-xs text-muted-foreground">
          {t("No units available at the parent level yet.")}
        </p>
      );
    }
    return (
      <div className="space-y-1">
        <Label htmlFor="unit-parent_id">{t("Parent ID")}</Label>
        <select
          id="unit-parent_id"
          value={valueOf("parent_id")}
          onChange={(e) =>
            setValues((current) => ({ ...current, parent_id: e.target.value }))
          }
          className="flex h-10 w-full items-center rounded-md border border-input bg-background px-3 py-2 text-sm"
        >
          {valueOf("parent_id") === "" && (
            <option value="">{t("Select…")}</option>
          )}
          {parentCandidates.map((candidate) => (
            <option key={candidate.id} value={candidate.id}>
              {parentOptionLabel(candidate)}
            </option>
          ))}
        </select>
      </div>
    );
  }

  // Muitos-para-muitos: uma ou mais Sales Organizations. O estado guarda os ids em CSV.
  function renderParentsField() {
    if (parentCandidates.length === 0) {
      return (
        <p className="text-xs text-muted-foreground">
          {t("No units available at the parent level yet.")}
        </p>
      );
    }
    const chosen = new Set(idsOf(valueOf("parent_ids")));
    const toggle = (id: string) => {
      const next = new Set(chosen);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      setValues((current) => ({ ...current, parent_ids: [...next].join(",") }));
    };
    return (
      <fieldset className="space-y-1">
        <legend className="text-sm font-medium">
          {t("Sales Organizations")}
        </legend>
        <div className="max-h-40 space-y-1 overflow-y-auto rounded-md border border-input p-2">
          {parentCandidates.map((candidate) => (
            <label
              key={candidate.id}
              className="flex items-center gap-2 text-sm"
            >
              <input
                type="checkbox"
                checked={chosen.has(candidate.id)}
                onChange={() => toggle(candidate.id)}
              />
              {parentOptionLabel(candidate)}
            </label>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">
          {t(
            "Choose at least one. Approving this unit does not approve its Sales Organizations.",
          )}
        </p>
      </fieldset>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isEdit
              ? t("Edit organizational unit")
              : t("New organizational unit")}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? t("Change the fields you want to update.")
              : t("All fields are required.")}
          </DialogDescription>
        </DialogHeader>

        {!isEdit && levelOptions.length > 1 && (
          <div className="space-y-1">
            <Label htmlFor="unit-level">{t("Level")}</Label>
            <select
              id="unit-level"
              value={selectedLevelCode}
              onChange={(e) => {
                // O rascunho é do tipo anterior: um subtype de Storage Location
                // seguiria num Shipping Point, invisível na tela e recusado
                // com 422 pelo backend.
                setSelectedLevelCode(e.target.value);
                setValues({});
              }}
              className="flex h-10 w-full items-center rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              {levelOptions.map((option) => (
                <option key={option.levelCode} value={option.levelCode}>
                  {t(option.levelName, { defaultValue: option.levelName })}
                </option>
              ))}
            </select>
          </div>
        )}

        {isEdit && idOnly && (
          <p className="text-xs text-muted-foreground">
            {t(
              "Only the ID and the fields that came empty from SAP can be edited for units extracted from SAP.",
            )}
          </p>
        )}
        {renderIdField()}
        {formDef.fields.map(renderField)}
        {parentLevelForType && !idOnly && parentControls}
        {renderParentField()}

        {restored && (
          <p className="text-xs text-muted-foreground">
            {t(
              "The ID correction will be removed and the extracted ID comes back.",
            )}
          </p>
        )}
        {error ? <p className="text-sm text-destructive">{error}</p> : null}

        <DialogFooter>
          {restorableKeys.length > 0 && !restored && (
            // Só existe na unidade extraída do SAP, e desfaz a sobreposição do
            // ID (o único campo que ela edita). Na manual não há valor extraído.
            <Button variant="outline" onClick={restoreExtractedValues}>
              {t("Restore extracted values")}
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("Cancel")}
          </Button>
          <Button onClick={handleSave} disabled={saveDisabled}>
            {t("Save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
